/**
 * Global Tabs Component
 * A reusable tabs component that can be used across different blocks
 *
 * Usage Examples:
 *
 * 1. Basic usage with default settings:
 *    const tabs = new TabsComponent();
 *
 * 2. Custom configuration:
 *    const tabs = new TabsComponent({
 *        containerSelector: '.my-tabs',
 *        buttonSelector: '[data-tab]',
 *        contentSelector: '[data-content]',
 *        activeClass: 'is-active',
 *        showMethod: 'block',
 *        hideMethod: 'none'
 *    });
 *
 * 3. Programmatic control:
 *    tabs.switchTab(container, 'tab-name');
 *    const activeTab = tabs.getActiveTab(container);
 *
 * HTML Structure:
 * <div class="tabs-container">
 *     <button data-filter="tab1" class="active">Tab 1</button>
 *     <button data-filter="tab2">Tab 2</button>
 *     <div data-tab-content="tab1">Content 1</div>
 *     <div data-tab-content="tab2">Content 2</div>
 * </div>
 */
export class TabsComponent {
	constructor(options = {}) {
		this.options = {
			containerSelector: '.tabs-container',
			buttonSelector: '[data-filter]',
			contentSelector: '[data-tab-content]',
			activeClass: 'active',
			showMethod: 'grid',
			hideMethod: 'none',
			...options,
		};

		this.init();
	}

	init() {
		// Wait for DOM to be ready
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setupTabs());
		} else {
			this.setupTabs();
		}
	}

	setupTabs() {
		const tabContainers = document.querySelectorAll(this.options.containerSelector);

		tabContainers.forEach((container) => {
			this.initializeContainer(container);
		});
	}

	initializeContainer(container) {
		const tabButtons = container.querySelectorAll(this.options.buttonSelector);
		const contentSections = container.querySelectorAll(this.options.contentSelector);

		if (!tabButtons.length || !contentSections.length) {
			return;
		}

		// Store references for this container
		container._tabsComponent = this;
		container._tabButtons = tabButtons;
		container._contentSections = contentSections;

		// Initialize first tab as active
		this.initializeFirstTab(container);

		// Add click event listeners to tab buttons
		tabButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				this.handleTabClick(button, container);
			});
		});
	}

	initializeFirstTab(container) {
		const firstButton = container.querySelector(`${this.options.buttonSelector}.${this.options.activeClass}`);
		const firstContent = container.querySelector(this.options.contentSelector);

		if (firstButton && firstContent) {
			// Show first content section
			this.showSection(firstContent);

			// Hide other content sections
			container._contentSections.forEach((section) => {
				if (section !== firstContent) {
					this.hideSection(section);
				}
			});
		}
	}

	handleTabClick(clickedButton, container) {
		const filter = clickedButton.getAttribute('data-filter');
		const targetContent = container.querySelector(`${this.options.contentSelector}[data-tab-content="${filter}"]`);

		if (!targetContent) {
			return;
		}

		// Update button states
		this.updateButtonStates(container._tabButtons, clickedButton);

		// Show target section and hide others
		this.showSection(targetContent);
		container._contentSections.forEach((section) => {
			if (section !== targetContent) {
				this.hideSection(section);
			}
		});
	}

	updateButtonStates(allButtons, activeButton) {
		allButtons.forEach((button) => {
			button.classList.remove(this.options.activeClass);
		});

		activeButton.classList.add(this.options.activeClass);
	}

	showSection(section) {
		section.style.display = this.options.showMethod;
		section.setAttribute('aria-hidden', 'false');
	}

	hideSection(section) {
		section.style.display = this.options.hideMethod;
		section.setAttribute('aria-hidden', 'true');
	}

	// Public method to programmatically switch tabs
	switchTab(container, tabFilter) {
		const button = container.querySelector(`${this.options.buttonSelector}[data-filter="${tabFilter}"]`);
		if (button) {
			this.handleTabClick(button, container);
		}
	}

	// Public method to get current active tab
	getActiveTab(container) {
		const activeButton = container.querySelector(`${this.options.buttonSelector}.${this.options.activeClass}`);
		return activeButton ? activeButton.getAttribute('data-filter') : null;
	}
}

// Create a default instance for general use
export const tabsComponent = new TabsComponent();

// Make TabsComponent available globally for use in PHP templates
window.TabsComponent = TabsComponent;

// Export for specific use cases
export default TabsComponent;
